import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Title,
  Divider,
  SimpleGrid,
  Alert,
  Loader,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";

function EditPersonalInfoModal({
  isOpen,
  onClose,
  userProfile,
  onUpdate,
  user,
}) {
  const { getVerifiedJWT, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    birthday: "",
    instagram: "",
    linkedin: "",
  });

  // Initialize form data when modal opens or userProfile changes
  useEffect(() => {
    if (userProfile && user && isOpen) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: userProfile.phone_number || "",
        birthday: userProfile.birthday
          ? userProfile.birthday.split("T")[0]
          : "",
        instagram: userProfile.instagram_username || "",
        linkedin: userProfile.linkedin_profile || "",
      });
    }
  }, [userProfile, user, isOpen]);

  const handleInputChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Format phone number as user types
  const handlePhoneChange = event => {
    const value = event.target.value.replace(/\D/g, ""); // Remove all non-digit characters
    let formattedValue = value;

    if (value.length >= 6) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    } else if (value.length >= 3) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    }

    setFormData(prev => ({
      ...prev,
      phoneNumber: formattedValue,
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      // Prepare API data - clean phone number for API
      const cleanPhoneNumber = formData.phoneNumber.replace(/\D/g, "");
      const apiData = {
        first_name: formData.firstName || null,
        last_name: formData.lastName || null,
        email: formData.email || null,
        phone_number: cleanPhoneNumber || null,
        birthday: formData.birthday || null,
        instagram_username: formData.instagram || null,
        linkedin_profile: formData.linkedin || null,
      };

      const response = await fetch(getApiEndpoint("/v1/user/profile"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        // Update auth user context with any changed name or email fields
        const authUserUpdates = {};
        if (formData.firstName !== user.firstName) {
          authUserUpdates.firstName = formData.firstName;
        }
        if (formData.lastName !== user.lastName) {
          authUserUpdates.lastName = formData.lastName;
        }
        if (formData.email !== user.email) {
          authUserUpdates.email = formData.email;
        }

        // If any auth user fields changed, update them
        if (Object.keys(authUserUpdates).length > 0) {
          updateUser(authUserUpdates);
        }

        // Trigger parent component to refetch fresh data from API
        onUpdate();
        onClose();
      } else {
        const data = await response.json();
        setError(data.message || "Failed to update personal information");
      }
    } catch (err) {
      setError("Failed to update personal information");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (userProfile && user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: userProfile.phone_number || "",
        birthday: userProfile.birthday
          ? userProfile.birthday.split("T")[0]
          : "",
        instagram: userProfile.instagram_username || "",
        linkedin: userProfile.linkedin_profile || "",
      });
    }
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      title={
        <Title order={3} fw={600}>
          Edit Personal Information
        </Title>
      }
      size="lg"
      padding="xl"
    >
      <Stack gap="xl">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {/* Contact Information Section */}
        <Stack gap="md">
          <Title order={4} c="dark" fw={600}>
            Contact Information
          </Title>
          <Divider />

          <SimpleGrid
            cols={2}
            spacing="md"
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          >
            <TextInput
              label="First Name"
              placeholder="First Name"
              value={formData.firstName}
              onChange={event => handleInputChange(event)}
              name="firstName"
              disabled={loading}
              required
            />
            <TextInput
              label="Last Name"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={event => handleInputChange(event)}
              name="lastName"
              disabled={loading}
              required
            />
          </SimpleGrid>

          <SimpleGrid
            cols={2}
            spacing="md"
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          >
            <TextInput
              label="Email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={event => handleInputChange(event)}
              name="email"
              disabled={loading}
              required
            />
            <TextInput
              label="Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phoneNumber}
              onChange={event => handlePhoneChange(event)}
              name="phoneNumber"
              disabled={loading}
              maxLength={14}
            />
          </SimpleGrid>

          <TextInput
            label="Birthday"
            type="date"
            value={formData.birthday}
            onChange={event => handleInputChange(event)}
            name="birthday"
            disabled={loading}
          />
        </Stack>

        {/* Social Media Section */}
        <Stack gap="md">
          <Title order={4} c="dark" fw={600}>
            Social Media
          </Title>
          <Divider />

          <SimpleGrid
            cols={2}
            spacing="md"
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          >
            <TextInput
              label="Instagram"
              placeholder="@username or profile URL"
              value={formData.instagram}
              onChange={event => handleInputChange(event)}
              name="instagram"
              disabled={loading}
            />
            <TextInput
              label="LinkedIn"
              placeholder="LinkedIn profile URL"
              value={formData.linkedin}
              onChange={event => handleInputChange(event)}
              name="linkedin"
              disabled={loading}
            />
          </SimpleGrid>
        </Stack>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={loading}
            leftSection={loading ? <Loader size="xs" /> : undefined}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditPersonalInfoModal;
